import { observer } from "mobx-react"
import { Box, Button, Stack } from "@mui/material"
import { t } from "@lingui/macro"
import React, { useCallback, useEffect } from "react"

import { CreateSection } from "./CreateSection"
import { CreatePropertyModalStore, IFormFields } from "./store"

import { ConfirmDeleteModal } from "src/modals/confirm-delete"
import { CopyPropertySettingsModal } from "src/modals/copy-property-settings"

import { IPropertyOwner } from "src/views/client-management/store"
import { AdminService } from "src/api"
import { admin_GetPropertyResponse } from "src/api/models/admin_GetPropertyResponse"
import { IProperty } from "src/views/client-management/property/store"
import { useStore } from "src/store/lib/useStore"
import { GlobalStore } from "src/store"
import { Form } from "src/components/Form"
import { ModalHeader } from "src/components/ModalHeader"
import { FormPanel } from "src/components/FormPanel"
import { StoreProvider } from "src/store/lib/StoreProvider"
import { useCloseConfirmationForForm } from "src/store/modals/use-close-confirmation-for-form"
import { CenteredSpinner } from "src/components/CenteredSpinner"

interface IProps {
    propertyOwnersList: IPropertyOwner[]
    chosenPropertyOwnerId?: number
    chosenLegalEntityId?: number
    id?: number | undefined
}

const CreateNewProperty = observer(
    ({
        propertyOwnersList,
        chosenPropertyOwnerId,
        chosenLegalEntityId,
        id,
    }: IProps) => {
        const globalStore = useStore(GlobalStore)
        const store = useStore(CreatePropertyModalStore)
        const [propertiesInLegalEntity, setPropertiesInLegalEntity] =
            React.useState<IProperty[]>([])
        const [propertiesInPropertyOwner, setPropertiesInPropertyOwner] =
            React.useState<IProperty[]>([])

        useCloseConfirmationForForm(store.fields)

        useEffect(() => {
            ;(async () => {
                await store.init(chosenPropertyOwnerId, chosenLegalEntityId, id)

                // Load properties for copy settings
                if (
                    id != null &&
                    store.fields.data.legalEntityId != null &&
                    store.fields.data.propertyOwnerId != null
                ) {
                    try {
                        // Load properties in the same legal entity
                        const legalEntityProperties =
                            await AdminService.postV1AdminPropertySearch({
                                request: {
                                    legal_entity_ids: [
                                        store.fields.data.legalEntityId,
                                    ],
                                    property_owner_ids: [
                                        store.fields.data.propertyOwnerId,
                                    ],
                                },
                            })

                        // Load ALL properties in the property owner
                        const propertyOwnerProperties =
                            await AdminService.postV1AdminPropertySearch({
                                request: {
                                    property_owner_ids: [
                                        store.fields.data.propertyOwnerId,
                                    ],
                                },
                            })

                        const mapProperty = (
                            prop: admin_GetPropertyResponse,
                        ): IProperty => ({
                            city: prop.city ?? "",
                            createdDate: prop.created_date ?? "",
                            externalId: prop.external_id ?? "",
                            integrationId: prop.integration_id ?? undefined,
                            integrationActive: prop.integration_active ?? false,
                            integrationType: prop.integration_type ?? "",
                            leaflet: prop.leaflet ?? "",
                            legalEntityId: prop.legal_entity_id ?? 0,
                            modifiedDate: prop.modified_date ?? "",
                            name: prop.name ?? "",
                            propertyId: prop.property_id ?? 0,
                            propertyOwnerId: prop.property_owner_id ?? 0,
                            selfOnboardingCode: prop.self_onboarding_code ?? "",
                            selfOnboardingEnabled:
                                prop.self_onboarding_enabled ?? false,
                            id: prop.property_id ?? 0,
                        })

                        // Filter out the current property from both lists
                        setPropertiesInLegalEntity(
                            legalEntityProperties
                                .filter((prop) => prop.property_id !== id)
                                .map(mapProperty),
                        )

                        setPropertiesInPropertyOwner(
                            propertyOwnerProperties
                                .filter((prop) => prop.property_id !== id)
                                .map(mapProperty),
                        )
                    } catch (e) {
                        // Silently fail, copy settings just won't be available
                        setPropertiesInLegalEntity([])
                        setPropertiesInPropertyOwner([])
                    }
                }
            })()
        }, [store, chosenPropertyOwnerId, chosenLegalEntityId, id])

        const isLoading =
            globalStore.loading.is(CreatePropertyModalStore.LoadingKeys.init) ||
            globalStore.loading.is(
                CreatePropertyModalStore.LoadingKeys.submit,
            ) ||
            globalStore.loading.is(CreatePropertyModalStore.LoadingKeys.delete)

        const validateRequiredFields = useCallback(
            (requiredFields: string[]) => {
                for (const field of requiredFields) {
                    const requiredField = field as keyof IFormFields
                    const formField = store.fields.get(requiredField)

                    if (formField == null || formField === "") {
                        store.fields.setError(requiredField, t`errors.required`)
                    } else {
                        store.fields.clearError(requiredField)
                    }
                }
            },
            [store.fields],
        )

        const handleSubmit = useCallback(async () => {
            let requiredFields = ["propertyOwnerId", "legalEntityId", "name"]

            validateRequiredFields(requiredFields)
            if (store.fields.hasErrors()) {
                return
            }
            await store.submit()

            if (!store.fields.hasErrors()) {
                globalStore.modals.pop()
            }
        }, [store, globalStore, validateRequiredFields])

        const deleteEntity = useCallback(
            async (id: number | string) => {
                await store.delete(id)
                globalStore.modals.pop()
                globalStore.modals.pop()
            },
            [store, globalStore],
        )

        const handleDeleteEntityCall = useCallback(async () => {
            if (store.fields.data.id !== undefined) {
                await globalStore.modals.open(
                    () => (
                        <ConfirmDeleteModal
                            variant="single"
                            deleteItem={deleteEntity}
                            itemId={store.fields.data.id ?? 0}
                            title={t`common.delete`}
                            content={t`create-new-property-modal.delete-warning`}
                        />
                    ),
                    { variant: "slide-up-w600" },
                )
            }
        }, [deleteEntity, globalStore.modals, store.fields.data.id])

        const handleCopySettingsClick = useCallback(() => {
            globalStore.modals.open(
                () => (
                    <CopyPropertySettingsModal
                        properties={propertiesInLegalEntity}
                        settings={{
                            integrationActive:
                                store.fields.data.integrationActive,
                        }}
                        sourcePropertyName={store.fields.data.name}
                        description={t`create-new-property-modal.copy-settings-legal-entity-description`}
                    />
                ),
                { variant: "slide-up-w600" },
            )
        }, [
            globalStore.modals,
            propertiesInLegalEntity,
            store.fields.data.integrationActive,
            store.fields.data.name,
        ])

        const handleCopySettingsToPropertyOwnerClick = useCallback(() => {
            globalStore.modals.open(
                () => (
                    <CopyPropertySettingsModal
                        properties={propertiesInPropertyOwner}
                        settings={{
                            integrationActive:
                                store.fields.data.integrationActive,
                        }}
                        sourcePropertyName={store.fields.data.name}
                        description={t`create-new-property-modal.copy-settings-property-owner-description`}
                    />
                ),
                { variant: "slide-up-w600" },
            )
        }, [
            globalStore.modals,
            propertiesInPropertyOwner,
            store.fields.data.integrationActive,
            store.fields.data.name,
        ])

        if (isLoading) {
            return <CenteredSpinner height="100vh" />
        }

        return (
            <>
                <Stack spacing={2}>
                    <Form onSubmit={handleSubmit}>
                        <ModalHeader>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={store.isLoading}
                                data-testid="save-button"
                            >
                                {t`create-new-property-modal.save-button`}
                            </Button>
                        </ModalHeader>
                        <FormPanel
                            sections={[
                                {
                                    header: Boolean(store.fields.data.id)
                                        ? t`create-new-property-modal.section-header.edit-property`
                                        : t`create-new-property-modal.section-header.create-new-property`,
                                    content: (
                                        <CreateSection
                                            propertyOwnersList={
                                                propertyOwnersList
                                            }
                                        />
                                    ),
                                },
                            ]}
                        />
                    </Form>
                    {store.fields.data.id !== undefined && (
                        <FormPanel
                            sections={[
                                {
                                    header: t`create-new-property-modal.copy-settings-header`,
                                    content: (
                                        <>
                                            <div>{t`create-new-property-modal.copy-settings-description`}</div>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    gap: 1,
                                                    mt: 2,
                                                }}
                                            >
                                                <Button
                                                    variant="contained"
                                                    data-testid={
                                                        "copy-settings-button"
                                                    }
                                                    onClick={
                                                        handleCopySettingsClick
                                                    }
                                                    disabled={
                                                        propertiesInLegalEntity.length ===
                                                        0
                                                    }
                                                >
                                                    {t`create-new-property-modal.copy-settings-button`}
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    data-testid={
                                                        "copy-settings-property-owner-button"
                                                    }
                                                    onClick={
                                                        handleCopySettingsToPropertyOwnerClick
                                                    }
                                                    disabled={
                                                        propertiesInPropertyOwner.length ===
                                                        0
                                                    }
                                                >
                                                    {t`create-new-property-modal.copy-settings-property-owner-button`}
                                                </Button>
                                            </Box>
                                        </>
                                    ),
                                },
                            ]}
                        />
                    )}
                    {store.fields.data.id !== undefined && (
                        <FormPanel
                            sections={[
                                {
                                    header: "Delete",
                                    content: (
                                        <>
                                            <div>{t`create-new-property-modal.delete-warning`}</div>
                                            <Button
                                                type="submit"
                                                style={{ marginRight: "auto" }}
                                                variant="contained"
                                                data-testid={"delete-button"}
                                                onClick={handleDeleteEntityCall}
                                            >
                                                {t`common.delete`}
                                            </Button>
                                        </>
                                    ),
                                },
                            ]}
                        />
                    )}
                </Stack>
            </>
        )
    },
)

export const CreateNewPropertyModal = ({
    propertyOwnersList,
    chosenPropertyOwnerId,
    chosenLegalEntityId,
    id,
}: IProps) => (
    <StoreProvider Store={CreatePropertyModalStore}>
        <CreateNewProperty
            chosenPropertyOwnerId={chosenPropertyOwnerId}
            propertyOwnersList={propertyOwnersList}
            chosenLegalEntityId={chosenLegalEntityId}
            id={id}
        />
    </StoreProvider>
)
